import React, { useMemo, useCallback } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { DropPoint } from '@m4a3/api-client';
import { MARKER_STYLES } from '../../constants/mapConfig';

export interface DropPointLayerProps {
  dropPoints: DropPoint[];
  selectedDropPointId: string | null;
  preferredDropPointId?: string | null;
  onDropPointClick: (dropPoint: DropPoint) => void;
  // Optional viewport bounds for culling
  viewportBounds?: L.LatLngBounds;
}

// Create icon cache to avoid recreating icons on every render
const iconCache = new Map<string, L.DivIcon>();

const createCachedDropPointIcon = (isSelected: boolean, isDeleted: boolean, isVerified: boolean, isPreferred: boolean): L.DivIcon => {
  const cacheKey = `${isSelected}-${isDeleted}-${isVerified}-${isPreferred}`;
  
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!;
  }
  
  const size = isSelected ? MARKER_STYLES.SELECTED_SIZE : MARKER_STYLES.DEFAULT_SIZE;
  let backgroundColor = MARKER_STYLES.UNVERIFIED_COLOR; // Default blue for unverified
  
  if (isDeleted) {
    backgroundColor = MARKER_STYLES.DELETED_COLOR; // Gray for deleted
  } else if (isVerified) {
    backgroundColor = MARKER_STYLES.VERIFIED_COLOR; // Purple for verified
  } else {
    backgroundColor = MARKER_STYLES.UNVERIFIED_COLOR; // Blue for unverified
  }
  
  // Create star overlay for preferred drop points
  const starOverlay = isPreferred ? `
    <div style="
      position: absolute;
      top: -6px;
      right: -6px;
      width: 16px;
      height: 16px;
      background-color: #FFD700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.4);
      z-index: 10;
    ">
      <span style="color: white; font-size: 10px; font-weight: bold;">★</span>
    </div>
  ` : '';
  
  const icon = L.divIcon({
    html: `<div style="position: relative;">
      <div style="
        background-color: ${backgroundColor};
        border: 2px solid ${isPreferred ? '#FFD700' : 'white'};
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${isSelected ? 'transform: scale(1.2); z-index: 1000;' : ''}
        ${isDeleted ? 'opacity: 0.6;' : ''}
      "></div>
      ${starOverlay}
    </div>`,
    className: `circular-drop-point-marker ${isSelected ? 'selected' : ''} ${isDeleted ? 'deleted' : ''} ${isPreferred ? 'preferred' : ''}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
  
  iconCache.set(cacheKey, icon);
  return icon;
};

// Memoized individual drop point marker component
const DropPointMarker: React.FC<{
  dropPoint: DropPoint;
  isSelected: boolean;
  isPreferred: boolean;
  onDropPointClick: (dropPoint: DropPoint) => void;
}> = React.memo(({ dropPoint, isSelected, isPreferred, onDropPointClick }) => {
  const icon = useMemo(() => 
    createCachedDropPointIcon(isSelected, dropPoint.isDeleted || false, dropPoint.verified, isPreferred),
    [isSelected, dropPoint.isDeleted, dropPoint.verified, isPreferred]
  );

  const handleClick = useCallback(() => {
    if (!dropPoint.isDeleted) {
      onDropPointClick(dropPoint);
    }
  }, [dropPoint, onDropPointClick]);

  return (
    <Marker
      position={[dropPoint.latitude, dropPoint.longitude]}
      icon={icon}
      eventHandlers={{
        click: (e: any) => {
          try {
            if (e?.originalEvent) {
              e.originalEvent.preventDefault?.();
              e.originalEvent.stopPropagation?.();
            }
          } catch {}
          handleClick();
        },
      }}
    />
  );
});

DropPointMarker.displayName = 'DropPointMarker';

// Spatial filtering function to only render markers in viewport
const filterDropPointsByViewport = (
  dropPoints: DropPoint[],
  viewportBounds?: L.LatLngBounds
): DropPoint[] => {
  if (!viewportBounds) {
    return dropPoints;
  }

  // Add padding to bounds to include markers just outside viewport
  const paddedBounds = viewportBounds.pad(0.1);
  
  return dropPoints.filter(dropPoint => 
    paddedBounds.contains([dropPoint.latitude, dropPoint.longitude])
  );
};

const DropPointLayer: React.FC<DropPointLayerProps> = React.memo(({
  dropPoints,
  selectedDropPointId,
  preferredDropPointId,
  onDropPointClick,
  viewportBounds,
}) => {
  // Filter drop points by viewport for performance
  const visibleDropPoints = useMemo(() => 
    filterDropPointsByViewport(dropPoints, viewportBounds),
    [dropPoints, viewportBounds]
  );

  // Group drop points by status for better rendering performance
  const groupedDropPoints = useMemo(() => {
    const active: DropPoint[] = [];
    const deleted: DropPoint[] = [];
    
    visibleDropPoints.forEach(dp => {
      if (dp.isDeleted) {
        deleted.push(dp);
      } else {
        active.push(dp);
      }
    });
    
    return { active, deleted };
  }, [visibleDropPoints]);

  if (!Array.isArray(dropPoints) || dropPoints.length === 0) {
    return null;
  }

  return (
    <>
      {/* Render active drop points first (higher z-index) */}
      {groupedDropPoints.active.map(dropPoint => (
        <DropPointMarker
          key={`active-${dropPoint.id}`}
          dropPoint={dropPoint}
          isSelected={dropPoint.id === selectedDropPointId}
          isPreferred={dropPoint.id === preferredDropPointId}
          onDropPointClick={onDropPointClick}
        />
      ))}
      
      {/* Render deleted drop points last (lower z-index) */}
      {groupedDropPoints.deleted.map(dropPoint => (
        <DropPointMarker
          key={`deleted-${dropPoint.id}`}
          dropPoint={dropPoint}
          isSelected={dropPoint.id === selectedDropPointId}
          isPreferred={dropPoint.id === preferredDropPointId}
          onDropPointClick={onDropPointClick}
        />
      ))}
    </>
  );
});

DropPointLayer.displayName = 'DropPointLayer';

export default DropPointLayer;
